import { stateAbbreviations } from 'app/SeFriendly/validateUsaState'
import { PROVIDER_SEARCH_LOCATION_PLACE_TYPES } from 'constants/common'

export type UsStateRef = {
  code: string
  name: string
}

export type ProviderSearchPlacesRequest = {
  input: string
  types: string[]
}

const STATE_PLACE_TYPE = 'administrative_area_level_1'

const STATE_BY_CODE: Record<string, UsStateRef> = Object.fromEntries(
  Object.entries(stateAbbreviations).map(([code, name]) => [
    code,
    { code, name },
  ])
)

const STATE_BY_NAME: Record<string, UsStateRef> = Object.fromEntries(
  Object.values(STATE_BY_CODE).map((state) => [state.name.toLowerCase(), state])
)

const isCountryToken = (token: string): boolean => {
  const normalized = token.trim().toLowerCase()

  return ['usa', 'us', 'united states', 'united states of america'].includes(
    normalized
  )
}

/**
 * Returns a US state when the location string refers to the state itself
 * (e.g. "Texas, TX, USA", "Texas", "TX"), not a city within it
 * (e.g. "Austin, TX, USA", "Texas City, TX").
 */
export const parseStateOnlyLocationQuery = (
  location: string
): UsStateRef | null => {
  if (!location?.trim()) {
    return null
  }

  const parts = location
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)

  if (parts.length === 0) {
    return null
  }

  if (parts.length > 1 && isCountryToken(parts[parts.length - 1])) {
    parts.pop()
  }

  if (parts.length === 1) {
    const token = parts[0]
    const byCode = STATE_BY_CODE[token.toUpperCase()]

    if (byCode) {
      return byCode
    }

    return STATE_BY_NAME[token.toLowerCase()] ?? null
  }

  if (parts.length === 2) {
    const [maybeName, maybeCode] = parts
    const stateFromCode = STATE_BY_CODE[maybeCode.toUpperCase()]

    if (
      stateFromCode &&
      stateFromCode.name.toLowerCase() === maybeName.toLowerCase()
    ) {
      return stateFromCode
    }

    return null
  }

  return null
}

/**
 * Builds the Places Autocomplete request for provider-search auto-select.
 * State-only queries ask Google for administrative_area_level_1 so we get
 * "Texas, USA" instead of a random city/zip that happens to rank first.
 */
export const getProviderSearchPlacesRequest = (
  location: string
): ProviderSearchPlacesRequest => {
  const stateOnly = parseStateOnlyLocationQuery(location)

  if (stateOnly) {
    return {
      input: stateOnly.name,
      types: [STATE_PLACE_TYPE],
    }
  }

  return {
    input: location,
    types: PROVIDER_SEARCH_LOCATION_PLACE_TYPES,
  }
}

/**
 * Safety net: if predictions still include a matching state, prefer it.
 */
export const pickProviderSearchLocationPrediction = <
  T extends {
    types?: string[]
    structured_formatting?: { main_text?: string }
  }
>(
  predictions: T[],
  location: string
): T => {
  const stateOnly = parseStateOnlyLocationQuery(location)

  if (stateOnly) {
    const statePrediction = predictions.find(
      (prediction) =>
        prediction.types?.includes(STATE_PLACE_TYPE) &&
        prediction.structured_formatting?.main_text?.toLowerCase() ===
          stateOnly.name.toLowerCase()
    )

    if (statePrediction) {
      return statePrediction
    }
  }

  return predictions[0]
}
